import {
  BoldItalicUnderlineToggles,
  listsPlugin,
  ListsToggle,
  MDXEditor,
  MDXEditorMethods,
  toolbarPlugin,
  UndoRedo,
} from "@mdxeditor/editor";
import Markdown from "react-markdown";
import "@mdxeditor/editor/style.css";
import {
  forwardRef,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  ActionIcon,
  Button,
  Group,
  Loader,
  Textarea as MantineTextarea,
  TextareaProps as MantineTextareaProps,
  Modal,
  Stack,
} from "@mantine/core";
import {
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import classes from "@/styles/TextInput.module.css";
import { useStyles } from "@/styles/useStyles";
import { Text } from "./Text";

type TextareaValue = string;

export interface TextareaProps extends Omit<MantineTextareaProps, "value"> {
  id: string;
  value: TextareaValue;
  defaultValue?: TextareaValue;
  onDebouncedChange?: (value: TextareaValue) => void;
  debounceDelay?: number;
  onClear?: () => void;
  withEditor?: boolean;
  readOnly?: boolean;
  noMaxHeight?: boolean;
  minCharacters?: number;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      id,
      onDebouncedChange,
      debounceDelay = 300,
      onClear,
      value,
      withEditor = false,
      readOnly = false,
      noMaxHeight = false,
      minCharacters = 0,
      ...props
    },
    ref,
  ) => {
    const { theme } = useStyles();
    const [localValue, setLocalValue] = useState<TextareaValue>(
      value || props.defaultValue || "",
    );
    const [isSaving, setIsSaving] = useState(false);
    const [forceRenderCount, setForceRenderCount] = useState(0);

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const mdxEditorRef = useRef<MDXEditorMethods | null>(null);
    const [expanded, setExpanded] = useState(false);
    const syncCheckRef = useRef<NodeJS.Timeout | null>(null);
    const isUserTyping = useRef(false);
    const lastPropValue = useRef(value);

    useEffect(() => {
      // Only force re-render if this is a genuine external change, not an echo from our own onChange
      const isExternalChange =
        value !== lastPropValue.current && !isUserTyping.current;

      setLocalValue(value || "");
      lastPropValue.current = value;

      // Only increment force render count for external changes or initial mount
      if (isExternalChange || forceRenderCount === 0) {
        setForceRenderCount((prev) => prev + 1);
      }
    }, [value, forceRenderCount]);

    // Check if MDXEditor's internal value is out of sync and force re-render if needed
    useEffect(() => {
      if (
        withEditor &&
        mdxEditorRef.current &&
        localValue &&
        !isUserTyping.current
      ) {
        // Clear any existing timeout
        if (syncCheckRef.current) {
          clearTimeout(syncCheckRef.current);
        }

        // Check sync after a short delay to allow MDXEditor to update
        syncCheckRef.current = setTimeout(() => {
          try {
            const editorValue = mdxEditorRef.current?.getMarkdown() || "";

            // If local value has content but editor is empty/undefined, force a re-render
            if (localValue && !editorValue) {
              console.log("MDXEditor sync issue detected, forcing re-render", {
                localValue,
                editorValue,
                forceRenderCount,
              });
              setForceRenderCount((prev) => prev + 1);
            }
          } catch (error) {
            console.warn("Error checking MDXEditor sync:", error);
            // If we can't check, force a re-render to be safe
            setForceRenderCount((prev) => prev + 1);
          }
        }, 100);
      }

      return () => {
        if (syncCheckRef.current) {
          clearTimeout(syncCheckRef.current);
        }
      };
    }, [localValue, withEditor]);

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = event.currentTarget.value;
        isUserTyping.current = true;
        setLocalValue(newValue);
        props.onChange?.(event);

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          onDebouncedChange?.(newValue);
          // Clear typing flag after debounced save
          setTimeout(() => {
            isUserTyping.current = false;
          }, 100);
        }, debounceDelay);
      },
      [props, debounceDelay, onDebouncedChange],
    );

    const handleClear = useCallback(() => {
      setLocalValue("");
      onClear?.();

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }, [onClear]);

    const visiblePlugins = useMemo(() => {
      const list = [listsPlugin()];

      list.push(
        toolbarPlugin({
          toolbarContents: () => {
            return (
              <>
                <UndoRedo />
                <BoldItalicUnderlineToggles />
                <ListsToggle />
              </>
            );
          },
        }),
      );

      return list;
    }, []);

    const renderEditor = () => {
      return (
        <Stack
          style={{
            border: `1px solid ${theme.colors.border[2]}`,
            borderRadius: theme.radius.md,
            overflow: "hidden",
          }}
          h={noMaxHeight ? "auto" : expanded ? "100%" : "auto"}
          mih={noMaxHeight ? "auto" : expanded ? "50vh" : "auto"}
          flex={1}
          gap={0}
        >
          {expanded && props.label && (
            <Group
              py="xs"
              px="md"
              justify="space-between"
              bg="background.1"
              style={{
                borderBottom: `1px solid ${theme.colors.border[2]}`,
                zIndex: 1000,
              }}
              pos="sticky"
              top={0}
            >
              <Text titleStyle="h4" c="text.8" flex={1} lineClamp={1}>
                {props.label}
              </Text>
              <ActionIcon
                variant="transparent"
                size="md"
                onClick={() => setExpanded(false)}
              >
                <IconX color={theme.colors.text[5]} />
              </ActionIcon>
            </Group>
          )}
          <Stack flex={1} h="100%">
            <MDXEditor
              key={`${id}-${forceRenderCount}`}
              ref={mdxEditorRef}
              className={`custom-editor ${noMaxHeight ? "" : expanded ? "custom-editor-expanded" : "custom-editor-collapsed"}`}
              contentEditableClassName={`prose ${
                expanded
                  ? "expanded"
                  : `height-${props.minRows ? props.minRows * 24 : 120}`
              }`}
              placeholder={props.placeholder}
              onChange={(newValue) => {
                isUserTyping.current = true;
                setLocalValue(newValue);
                if (timeoutRef.current) {
                  clearTimeout(timeoutRef.current);
                }
                timeoutRef.current = setTimeout(() => {
                  setIsSaving(true);
                  onDebouncedChange?.(newValue);

                  setTimeout(() => {
                    setIsSaving(false);
                    isUserTyping.current = false;
                  }, 1000);
                }, debounceDelay);
              }}
              markdown={localValue}
              plugins={visiblePlugins}
            />
          </Stack>
          <Group
            w="100%"
            justify="space-between"
            style={{ borderTop: `1px solid ${theme.colors.border[2]}` }}
            bg={theme.colors.background[1]}
            py={2}
            pos="sticky"
            bottom={0}
          >
            <Group px="md" align="center" gap="xs">
              {isSaving ? <Loader size={14} /> : <IconCheck size={14} />}
              <Text size="xs" c="text.6">
                {isSaving ? "Saving..." : "Saved"}
              </Text>
            </Group>
            {minCharacters > 0 && (
              <Group px="md" align="center" gap="xs">
                <Text size="xs" c="text.6">
                  <span
                    style={{
                      color:
                        localValue.length < minCharacters
                          ? theme.colors.red[8]
                          : theme.colors.green[8],
                    }}
                  >
                    {localValue.length} / {minCharacters}{" "}
                  </span>
                  <span style={{ fontSize: 10 }}>min. characters</span>
                </Text>
              </Group>
            )}
            <Button
              variant="transparent"
              color="text.8"
              size="xs"
              onClick={() => setExpanded(!expanded)}
              leftSection={
                expanded ? (
                  <IconArrowsMinimize size={14} />
                ) : (
                  <IconArrowsMaximize size={14} />
                )
              }
            >
              {expanded ? "Collapse" : "Expand"}
            </Button>
          </Group>
        </Stack>
      );
    };

    const renderExpandedEditor = () => {
      return (
        <Modal
          withCloseButton={false}
          opened={expanded}
          centered
          size="lg"
          onClose={() => setExpanded(false)}
          className="editorModal"
          styles={{
            body: {
              height: "80vh",
              // minHeight: "80vh",
              overflow: "hidden",
              padding: 0,
              // paddingBottom: 100
            },
          }}
        >
          {renderEditor()}
          {/* <IconArrowsMinimize
            style={{
              position: "absolute",
              bottom: 8,
              right: 8,
              opacity: 0.5,
              fontSize: 20,
            }}
          /> */}
        </Modal>
      );
    };

    if (readOnly) {
      return (
        <Stack
          id="markdown-preview"
          p="md"
          mah={noMaxHeight ? undefined : 300}
          gap={0}
          style={{
            border: `1px solid ${theme.colors.border[2]}`,
            borderRadius: theme.radius.md,
            overflow: "auto",
            backgroundColor: theme.colors.background[1],
            fontSize: 12,
          }}
        >
          <Markdown>{localValue}</Markdown>
        </Stack>
      );
    }
    if (withEditor) {
      if (expanded) {
        return renderExpandedEditor();
      }
      return renderEditor();
    }

    return (
      <MantineTextarea
        ref={ref}
        id={id}
        name={id}
        value={localValue}
        aria-invalid={props.error ? true : undefined}
        aria-errormessage={props.error ? `${id}-error` : undefined}
        onChange={handleChange}
        classNames={{ input: classes.textInput }}
        rightSection={
          value && onClear ? (
            <ActionIcon
              onClick={handleClear}
              variant="transparent"
              color={!value ? "text.4" : "text.6"}
            >
              <IconX />
            </ActionIcon>
          ) : (
            props.rightSection || null
          )
        }
        {...props}
        styles={{
          label: {
            color: "text.1",
            fontWeight: 700,
            // @ts-ignore
            ...props.styles?.label,
          },
          input: {
            fontSize: 12,
            // @ts-ignore
            ...props.styles?.input,
          },
        }}
      />
    );
  },
);

Textarea.displayName = "Textarea";
